// Domain aggregation + training-priority recommendations for a completed
// (or partially completed) Commercial Diagnostic run (plan.txt sections
// 74-75). Each of the 7 domains maps to exactly one battery section in
// this diagnostic (see commercialDiagnosticProfile.js's DOMAIN_BY_MODULE_ID),
// so a domain's score is just that section's own academyScore -- no
// cross-module weighting math is needed here.
//
// Explicitly does NOT compute or expose anything resembling a pass
// likelihood, pilot-suitability percentage, or ADAPT/airline percentile
// (section 74's "Do NOT calculate" / "Do NOT invent" list) -- only the
// per-domain scores themselves and comparative strongest/priority labels
// derived directly from them.
import { COMMERCIAL_DIAGNOSTIC_PROFILE, DIAGNOSTIC_DOMAINS } from '../data/commercialDiagnosticProfile';

const round1 = (value) => Math.round(value * 10) / 10;

// `run.completed` entries already carry {domain, overallScore} (see
// completeDiagnosticModule) so this only needs to re-key by domain and
// mark any domain whose section never completed as having no evidence.
export const aggregateDiagnosticDomainScores = (run) => {
  const scoreByDomain = new Map((run?.completed || []).map((entry) => [entry.domain, entry.overallScore]));
  return DIAGNOSTIC_DOMAINS.map((domain) => {
    const module = COMMERCIAL_DIAGNOSTIC_PROFILE.modules.find((item) => item.domain === domain);
    const score = scoreByDomain.get(domain);
    const hasEvidence = scoreByDomain.has(domain) && Number.isFinite(score);
    return {
      domain,
      moduleId: module?.moduleId || null,
      moduleName: module?.name || null,
      score: hasEvidence ? round1(score) : null,
      hasEvidence
    };
  });
};

// section 75: identify strongest established areas, training priorities,
// and areas without sufficient evidence. "Established" / "sufficient
// evidence" both mean the same thing here: the section actually completed
// and produced a finite score -- an abandoned run's remaining domains are
// reported as insufficient evidence, never silently scored as 0.
export const buildDiagnosticRecommendations = (domainScores) => {
  const withEvidence = domainScores.filter((entry) => entry.hasEvidence);
  const insufficientEvidence = domainScores.filter((entry) => !entry.hasEvidence);

  if (!withEvidence.length) {
    return { strongest: [], trainingPriority: [], insufficientEvidence };
  }

  const maxScore = Math.max(...withEvidence.map((entry) => entry.score));
  const minScore = Math.min(...withEvidence.map((entry) => entry.score));

  // Ties are possible and reported as such, rather than arbitrarily
  // picking one -- e.g. two domains both at the lowest score are both a
  // genuine training priority.
  const strongest = withEvidence.filter((entry) => entry.score === maxScore);
  const trainingPriority = withEvidence.length > 1
    ? withEvidence.filter((entry) => entry.score === minScore)
    : []; // a single completed domain has nothing to be relatively "lower" than

  return { strongest, trainingPriority, insufficientEvidence };
};

export const buildDiagnosticRecommendationText = (entry) => (
  `Your ${entry.moduleName} performance was lower than your other assessed areas. Recommended: ${entry.moduleName} Practice.`
);
